import React, { useState, useEffect, useContext } from 'react'
import { useRouter } from 'next/router'
import { FaApple } from 'react-icons/fa'
import Image from 'next/image'
import { Button, Input, Tabs, Tab, Card, CardBody, Link, Spinner } from "@heroui/react"

import Layout from '../components/layout'
import ContentArea from '../components/content-area'
import Header from '../components/header'
import Footer from '../components/footer'
import Divider from '../components/divider'
import SuccessCard from '../components/success-card'
import StatusChip from '../components/status-chip'
import { inputStyle } from '../src/styles'

import { UserContext } from '../contexts/user-context'
import localStorage from '../src/local-storage'

const Facebook = () => {
  const [hasFacebook, setHasFacebook] = useState(true)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  
  useEffect(() => {
    if (window.fbAsyncInit) return
    window.fbAsyncInit = () => {
			FB.init({
				appId      : '1855700951367915',
				cookie     : false,
				xfbml      : false,
				version    : 'v2.7'
			})
			// FB.AppEvents.logPageView()
			// window.fbApiInit = true
		}
		(function(d, s, id){
     var js, fjs = d.getElementsByTagName(s)[0];
     if (d.getElementById(id)) {return;}
     js = d.createElement(s); js.id = id;
     js.src = "https://connect.facebook.net/en_US/sdk.js";
     js.async = true
     fjs.parentNode.insertBefore(js, fjs);
    }(document, 'script', 'facebook-jssdk'))
	 
    setTimeout(() => {
      if (typeof(FB) === 'undefined') setHasFacebook(false)
    }, 250)
  }, [])

  const fbLogin = () => {
    if (typeof(FB) === 'undefined') return
    setLoading(true)
    FB.login((response) => {
      setLoading(false)
      if (response.authResponse) {
        FB.getLoginStatus((response) => {
          window.location.href = '/facebook?id=' + response.authResponse.userID + '&code=' + response.authResponse.accessToken;
        })
      } else {
        setError('Error logging in with Facebook')
      }

    }, {scope: 'public_profile,email'})
  }

  if (!hasFacebook) return
  return <div className='p-4'>
    <div className='rounded-md'>
      <StatusChip msg={error} color='danger' onClose={() => setError('')} />
        <Image alt='Facebook Login' 
          width={275} 
          height={43} 
          className='ml-auto mr-auto cursor-pointer h-auto mt-1 rounded-md' 
          onClick={() => {
            if (!loading) fbLogin()
          }} 
          src='/facebook-login.png' />
    </div>
    <div className='text-center text-gray-400 text-xl mt-4 mb-2'>OR</div>
  </div>
}

const Apple = ({ selected }) => {
  const [error, setError] = useState()
  const [hasApple, setHasApple] = useState(true)
  const [loading, setLoading] = useState(false)
  
  const router = useRouter()
  
  useEffect(() => {
    initApple()
  }, [])

  function initApple () {
    (function(d, s, id){
     var js, fjs = d.getElementsByTagName(s)[0];
     if (d.getElementById(id)) {return;}
     js = d.createElement(s); js.id = id;
     js.src = "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";
     js.async = true
     fjs.parentNode.insertBefore(js, fjs);
    }(document, 'script', 'apple-sdk'))

    setTimeout(() => {
      if (!window.AppleID) {
        setHasApple(false)
        return
      }
      window.AppleID.auth.init({
        clientId: 'com.login.challengehound',
        scope: 'name email',
        state: 'ch:web',
        redirectURI: `${window.location.protocol}//${window.location.host}/signin`,
        usePopup: true
      })
    }, 500)
  }

  if (!hasApple) return 
  const appleLogin = async () => {
    setLoading(true)
    try {
      const response = await window.AppleID.auth.signIn()
      try {
        const postConfig = {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(response.authorization)
        }

        const res = await fetch(
          `${process.env.API}/login/apple`,
          postConfig
        )

        if (res.ok) {
          const user = await res.json()
          
          // save user in local storage
          localStorage.setUser(user)

          // clear challenge cache
          localStorage.removeChallengeCache()
      
          if (user.isAdmin) localStorage.setAdminUser(user)
    
          const redirect = localStorage.getRedirect()
          if (redirect) {
            localStorage.removeRedirect()
            router.push(redirect)
          } else {
            router.push(`/challenges`)
          }
        } else {
          setError('Apple login failed - failed auth')
        }
      } catch (err) {
        console.log('catch error', err)
        setError(err)
      }
      setLoading(false)

    } catch (err) {
      console.log(err)
      setLoading(false)
      if (err.error === 'popup_closed_by_user') {
        setError('Apple login canceled')
      } else {
        setError(err.error)
      }
    }
  }

  return <div className='flex flex-col items-center'>
    <StatusChip msg={error} color='danger' onClose={() => setError('')} />
    <Button 
      size='lg'
      onPress={async () => { await appleLogin() }} 
      isDisabled={loading}
      startContent={<FaApple style={{ fontSize: '30px' }} />}
      className='bg-black text-white w-[280px]'
      >
      Sign {selected ==='signin' ? 'In' : 'Up'} with Apple
    </Button>
    <div className='text-center text-gray-400 text-xl mt-6 mb-2'>OR</div>
  </div>
}

const SignInForm = () => {
  const [error, setError] = useState()
  const [success, setSuccess] = useState(false)
  const [loading, setLoading] = useState(false)
  const [invalid, setInvalid] = useState({})
  const [data, setData] = useState({
    email: ''
  })

  const handleChange = (attr, value, isInt) => {
    if (invalid[attr]) {
      const newInvalid = { ...invalid }
      delete newInvalid[attr]
      setInvalid(newInvalid)
    }

    const toUpdate = Object.assign({}, data)
    toUpdate[attr] = value
    setSuccess(false)
    setError()
    setData(toUpdate)
  }

  const validate = () => {
    setSuccess(false)
    const validation = {}
    if (!data.email) validation.email = 'Required'
    
    if (!validation.email) {
      const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      if (!re.test(String(data.email).trim().toLowerCase())) {
        validation.email = 'Invalid Email'
      }
    }

    if (Object.keys(validation).length) {
      setInvalid(validation)
      return false
    }
    return true
  }

  const submit = async (e) => {
    if (!validate()) return
    setLoading(true)
    const toSubmit = Object.assign({}, data)
    toSubmit.email = toSubmit.email.trim()
    const localStorageRedirect = localStorage.getRedirect()
    if (localStorageRedirect) {
      toSubmit.redirect = localStorageRedirect
    }
    try {
      const postConfig = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(toSubmit)
      }
      const res = await fetch(`${process.env.API}/users/signin`, postConfig)
      if (res.status === 200) {
        setSuccess(true)
      } else {
        const err = await res.text()
        console.log(err)
        setError('Error signing in. Please try again.')
      }
      setLoading(false)
      setData({ email: '' })
    } catch (err) {
      console.log(err)
      setError(err)
      setLoading(false)
    }
  }

  return <>
    <ErrorBlock selected='signin' error={error} setError={setError} success={success} setSuccess={setSuccess} />
    
    <Input
      label='Email Address'
      name='email'
      placeholder=''
      required
      maxLength={50}
      value={data.email}
      isDisabled={loading}
      size='lg'
      radius='sm'
      className='mt-4 mb-4'
      classNames={ inputStyle }
      isInvalid={Boolean(invalid.email)}
      errorMessage={invalid.email}
      onChange={(e) => {
        handleChange('email', e.target.value)
      }} />

    <div className='w-full flex justify-end mb-12'>
      <Button
        size='lg'
        color='primary'
        isDisabled={loading}
        onPress={submit}
        >{loading ? 'Signing In...' : 'Sign In'}</Button>
    </div>
  </>
}

const SignUpForm = () => {
  const [error, setError] = useState('')
  const [invalid, setInvalid] = useState({})
  const [success, setSuccess] = useState(false)
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState({
    fname: '',
    lname: '',
    email: ''
  })

  const handleChange = (attr, value) => {
    if (invalid[attr]) {
      const newInvalid = { ...invalid }
      delete newInvalid[attr]
      setInvalid(newInvalid)
    }
    const toUpdate = Object.assign({}, data)
    toUpdate[attr] = value
    setSuccess(false)
    setError()
    setData(toUpdate)
  }

  const validate = () => {
    const validation = {}
    if (!data.fname) validation.fname = 'Required'
    if (!data.lname) validation.lname = 'Required'
    if (!data.email) validation.email = 'Required'
    
    if (!validation.email) {
      const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      if (!re.test(String(data.email).trim().toLowerCase())) {
        validation.email = 'Invalid Email'
      }
    }

    if (Object.keys(validation).length) {
      setInvalid(validation)
      return false
    }
    return true
  }

  const submit = async (e) => {
    setSuccess(false)
    if (!validate()) return
    setLoading(true)
    const toSubmit = Object.assign({}, data)
    toSubmit.email = toSubmit.email.trim()
    let cc = localStorage.getCookieConsent()
    if (typeof cc !== 'undefined') toSubmit.cookies = cc
    const localStorageRedirect = localStorage.getRedirect()
    if (localStorageRedirect) {
      toSubmit.redirect = localStorageRedirect
    }
    try {
      const postConfig = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(toSubmit)
      }
      const res = await fetch(`${process.env.API}/users/signup`, postConfig)
      if (res.status === 200) {
        setSuccess(true)
        setError('')
        setLoading(false)
        setData({
          fname: '',
          lname: '',
          email: ''
        })
      } else {
        const err = await res.text()
        console.log(err)
        setError('Error signing in. Please try again.')
      }
    } catch (err) {
      console.log(err)
      setError('Problem logging in')
      setLoading(false)
    }
  }

  return <>
    <ErrorBlock selected='signup' error={error} setError={setError} success={success} setSuccess={setSuccess} />
    
    <Input
      isInvalid={Boolean(invalid.fname)}
      errorMessage={invalid.fname}
      label='First Name'
      name='fname'
      placeholder=''
      required
      maxLength={50}
      value={data.fname}
      isDisabled={loading}
      size='lg'
      radius='sm'
      className='mt-4 mb-4'
      classNames={ inputStyle }
      onChange={(e) => {
        handleChange('fname', e.target.value)
      }} />

    <Input
      isInvalid={Boolean(invalid.lname)}
      errorMessage={invalid.lname}
      label='Last Name'
      name='lname'
      placeholder=''
      required
      maxLength={50}
      value={data.lname}
      isDisabled={loading}
      size='lg'
      radius='sm'
      className='mb-4'
      classNames={ inputStyle }
      onChange={(e) => {
        handleChange('lname', e.target.value)
      }} />
      
    <Input
      isInvalid={Boolean(invalid.email)}
      errorMessage={invalid.email}
      label='Email Address'
      name='email'
      placeholder=''
      required
      maxLength={50}
      value={data.email}
      isDisabled={loading}
      size='lg'
      radius='sm'
      className='mb-4'
      classNames={ inputStyle }
      onChange={(e) => {
        handleChange('email', e.target.value)
      }} />

    <div className='flex gap-2 mb-12'>
      <div className='text-sm grow'>
        By signing up you are agreeing to our<br/><Link className='text-sm' href='/terms'>Terms of Service</Link> and <Link className='text-sm' href='/privacy'>Privacy Policy</Link>
      </div>
      <Button
        size='lg'
        color='primary'
        isDisabled={loading}
        onPress={submit}
        >{loading ? 'Signing Up...' : 'Sign Up'}</Button>
    </div>
  </>
}

// this component is used by email forms, not buttons
const ErrorBlock = ({ selected, error, setError, success, setSuccess }) => {
  return <div>
    <StatusChip className='h-auto whitespace-normal p-2 max-w-full' color='danger' msg={error} onClose={() => {
      setError('')
    }} />

    {success && <SuccessCard closeFn={() => { setSuccess(false) }}>
      <div className='text-center font-bold mb-2'>CHECK YOUR EMAIL</div>
      
      <div className='text-center'>
        A sign in link has been sent.

        {selected === 'signin' && <p className='mt-2'>
          If you <strong>do not</strong> receive an email, you<br/>may need to <Link className='text-info' href={'/signup'}>Sign Up</Link>.
        </p>}
      </div>
    </SuccessCard>}
  </div>
}

const SignIn = () => {
  const userHook = useContext(UserContext)
  const user = userHook.user
  
  // get id
  const router = useRouter()
  const { err, route } = router.query
  const errorMsg = err

  const [selected, setSelected] = useState('')
  
  useEffect(() => {
    if (!user.token || !userHook.loggedIn) {
      localStorage.removeUser()
    }

    if (userHook.loggedIn) {
      router.push(`/challenges`)
    }
    
    // redirect to right tab based on URL path
    setTimeout(() => {
      setSelected(window.location.pathname.replace(/^\//, ''))
    }, 100)
  }, [])

  return (
    <Layout
      title='Challenge Hound: Sign In'
      description='Log In to Challenge Hound - an amazing platform to create and track group challenges.'
      keywords='create group challenges sign in login log in'>
      <Header section='public' subsection='signin' />
      <main>
        <ContentArea>
          <Divider isTop>Sign {selected === 'signin' ? 'in to' : 'up with'} Challenge Hound</Divider>
          
          <div className='text-center'><StatusChip className='h-auto whitespace-normal p-2 mb-4 max-w-full' color='danger' msg={errorMsg} /></div>

          <Card className='max-w-[420px] mx-auto mt-8 bg-chCard-background'>
            <CardBody>
              
              <Tabs
                color='secondary'
                fullWidth
                size="md"
                aria-label="Sign in or Sign Up"
                selectedKey={selected}
                onSelectionChange={setSelected}
                classNames={{ panel: 'pt-16', tabList: 'bg-chLight', tab: '[&>div]:text-chDarkBlue' }}
              >
                <Tab key='signin' title='Sign In'>
                  
                  <Apple selected={selected} />
                  <Facebook />

                  <SignInForm />

                  <div className='text-center'>
                    Don&apos;t have an account? <Link className='cursor-pointer' size="sm" onPress={() => setSelected('signup')}>Sign Up</Link>
                  </div>

                </Tab>
                <Tab key='signup' title='Sign up'>
                  
                  <Apple selected={selected} />
                  <Facebook  />

                  <SignUpForm />

                  <div className='text-center'>
                    Already have an account? <Link className='cursor-pointer' size="sm" onPress={() => setSelected('signin')}>Sign In</Link>
                  </div>


                </Tab>
              </Tabs>
            
            </CardBody>
          </Card>
          

        </ContentArea>
      </main>
      <Footer />
    </Layout>
  )
}

export default SignIn
